/**
 * Discord の Activity（音声チャンネルの中で開く Web ページ。Embedded App SDK）で補正サーバーの映像をみんなで見る（Issue #687）。
 *
 * Discord はページを `https://<アプリの ID>.discordsays.com/?frame_id=…&instance_id=…&platform=…` で開き、開発者ポータルの
 * URL の対応づけ（`/` → `www.ds.synchro-3384.com`）で本番へ中継する。ページの中からは同じ origin の道しか通らないので、
 * 映像は相対の `/live/stream`（CloudFront の /live/* → 補正サーバー）をそのまま読む。アプリの ID はホスト名から読む（コードに書かない）。
 */

const HOST_SUFFIX = '.discordsays.com';

/** Discord の Activity の中で開かれているか（ホスト名が `<ID>.discordsays.com`） */
export function inDiscordActivity(loc = window.location) {
  return String(loc?.hostname ?? '').endsWith(HOST_SUFFIX);
}

/** アプリの ID（ホスト名の頭）。Activity の外なら null */
export function clientIdFromHost(hostname) {
  const h = String(hostname ?? '');
  if (!h.endsWith(HOST_SUFFIX)) return null;
  const id = h.slice(0, -HOST_SUFFIX.length);
  return /^\d+$/.test(id) ? id : null;
}

/** SDK の初期化に要る問い合わせの引数（frame_id）があるか。無いと SDK の constructor が投げる */
export function hasSdkParams(search) {
  return new URLSearchParams(search ?? '').has('frame_id');
}

/** 見るもの: 実写（補正サーバーの合成）/ ゲーム映像（描く側が送り返したもの。録画中の録画のもの） */
export const VIEWS = [
  { key: 'live', label: '実写', url: '/live/stream' },
  { key: 'game', label: 'ゲーム', url: '/live/stream?fx=1' },
];

export function viewUrl(key) {
  return (VIEWS.find((v) => v.key === key) ?? VIEWS[0]).url;
}

/** 上下の切り抜き（映像の高さに対する割合）。1 辺の上限と、残す高さの下限 */
export const CROP_MAX = 0.45;
const CROP_KEY = 'discordActivity.crop';

/** その端末の保存場所（読めない・ブロックされていれば null） */
function localStore() {
  try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; }
}

/** 上下の切り抜きを収める（数でなければ 0。1 辺 0〜CROP_MAX） */
export function clampCrop(c) {
  const one = (v) => Math.min(CROP_MAX, Math.max(0, Number.isFinite(Number(v)) ? Number(v) : 0));
  return { top: one(c?.top), bottom: one(c?.bottom) };
}

/** その端末に保存した切り抜き（見るもの＝実写 / ゲームごと。読めなければ切らない） */
export function loadCrop(view, storage = localStore()) {
  try { return clampCrop(JSON.parse(storage?.getItem(CROP_KEY) || '{}')[view]); } catch { return clampCrop(null); }
}

export function saveCrop(view, crop, storage = localStore()) {
  try {
    const all = JSON.parse(storage?.getItem(CROP_KEY) || '{}');
    storage?.setItem(CROP_KEY, JSON.stringify({ ...all, [view]: clampCrop(crop) }));
  } catch { /* 残せなくても見られる */ }
}

/**
 * 切り抜いた枠の形: 枠の縦横比（幅 / 残す高さ）と、枠の中の映像の高さ・上の位置（枠の高さに対する %）。
 * 枠は画面に収め（contain）、はみ出した上下は枠が隠す
 */
export function cropFrame(width, height, crop) {
  const { top, bottom } = clampCrop(crop);
  const keep = 1 - top - bottom;
  const w = width > 0 ? width : 1080;
  const h = height > 0 ? height : 1940;
  return { ratio: w / (h * keep), heightPct: 100 / keep, topPct: (-top / keep) * 100 };
}
